import React, { useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { NewsArticle } from '../../types';
import NewsCard from './NewsCard';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface NewsSliderProps {
  news: NewsArticle[];
  loading?: boolean;
  title?: string;
}

const NewsSlider: React.FC<NewsSliderProps> = ({
  news,
  loading = false,
  title = "Son Haberler"
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigationPrevRef = useRef<HTMLButtonElement>(null);
  const navigationNextRef = useRef<HTMLButtonElement>(null);

  if (loading) {
    return (
      <Box sx={{ mb: 8 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 4, fontWeight: 700 }}>
          {title}
        </Typography>
        <Box sx={{
          backgroundColor: 'background.paper',
          borderRadius: 3,
          p: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Box
                key={i}
                sx={{
                  width: { xs: 240, sm: 280, md: 300 },
                  height: 250,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  flexShrink: 0
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  if (!news || news.length === 0) {
    return (
      <Box sx={{ mb: 8 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 4, fontWeight: 700 }}>
          {title}
        </Typography>
        <Box sx={{
          backgroundColor: 'background.paper',
          borderRadius: 3,
          p: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          textAlign: 'center',
          minHeight: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          <Typography variant="body1" color="text.secondary">
            Henüz haber bulunmamaktadır.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 8 }}>
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 4, fontWeight: 700 }}>
        {title}
      </Typography>

      <Box sx={{
        position: 'relative',
        backgroundColor: 'background.paper',
        borderRadius: 3,
        p: 3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        maxWidth: '100%',
        '& .swiper': {
          overflow: 'visible',
          paddingBottom: '40px'
        }
      }}>
        <IconButton
          ref={navigationPrevRef}
          sx={{
            position: 'absolute',
            left: 20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(255,255,255,0.9)',
            boxShadow: 2,
            width: 40,
            height: 40,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.95)',
            },
            display: { xs: 'none', sm: 'flex' }
          }}
        >
          <ChevronLeft />
        </IconButton>

        <IconButton
          ref={navigationNextRef}
          sx={{
            position: 'absolute',
            right: 20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(255,255,255,0.9)',
            boxShadow: 2,
            width: 40,
            height: 40,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.95)',
            },
            display: { xs: 'none', sm: 'flex' }
          }}
        >
          <ChevronRight />
        </IconButton>

        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={isMobile ? 16 : 24}
          slidesPerView={isMobile ? 1.2 : 'auto'}
          navigation={{
            prevEl: navigationPrevRef.current,
            nextEl: navigationNextRef.current,
          }}
          pagination={{
            clickable: true,
            dynamicBullets: true,
          }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          loop={true}
          centeredSlides={false}
          style={{ padding: '0 10px' }}
        >
          {news.map((article, index) => (
            <SwiperSlide
              key={article.id}
              style={{
                width: isMobile ? 240 : 300,
                height: 'auto'
              }}
            >
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <NewsCard article={article} showCategory={false} />
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
    </Box>
  );
};

export default NewsSlider;